import type { CardPayment } from '../../types/cardRefund';

// Regras puras da tela de estorno de cartão. O backend valida de novo — aqui é
// só para o botão não deixar mandar o que vai ser recusado.

/** Formata centavos como "R$ 1.234,56". */
export const formatCentsBRL = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** "2026-10-01T10:05:09" → "01/10/2026 10:05" (lendo o texto, sem Date). */
export const formatIsoDateTimeBR = (iso?: string): string => {
  if (!iso || iso.length < 10) return '—';
  const out = `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  return iso.length >= 16 ? `${out} ${iso.slice(11, 16)}` : out;
};

/** Venda capturada e com saldo: pode ser estornada. */
export const isRefundable = (p: Pick<CardPayment, 'status' | 'refundable_cents'>): boolean =>
  p.status === 2 && p.refundable_cents > 0;

export type RefundMode = 'total' | 'partial';

/**
 * Valor que será estornado, ou a mensagem de por que não pode.
 * `typedCents` é o valor digitado no parcial (null = vazio).
 */
export const resolveRefundAmount = (
  p: Pick<CardPayment, 'status' | 'refundable_cents'>,
  mode: RefundMode,
  typedCents: number | null,
): { cents: number } | { error: string } => {
  if (p.status !== 2) return { error: 'Só é possível estornar venda capturada.' };
  if (p.refundable_cents <= 0) return { error: 'Esta venda já foi estornada integralmente.' };
  if (mode === 'total') return { cents: p.refundable_cents };
  if (typedCents == null || typedCents <= 0) return { error: 'Informe o valor do estorno.' };
  if (typedCents > p.refundable_cents) {
    return { error: `O valor passa do saldo estornável (${formatCentsBRL(p.refundable_cents)}).` };
  }
  return { cents: typedCents };
};

/** Máscara de centavos: o usuário digita só números ("12345" → 123,45). */
export const typedToCents = (typed: string): number | null => {
  const digits = typed.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  return digits === '' ? null : Number(digits);
};

/** Exibe centavos no campo como "1.234,56" ('' quando vazio). */
export const centsToMaskedInput = (cents: number | null): string =>
  cents == null ? '' : (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Rótulo da bandeira para a tela. */
export const brandLabel = (brand: string): string => (brand || 'Cartão').toUpperCase();

/**
 * O pedido pode ter cobrança no PaymentHub: veio do site (número numérico) ou de
 * proposta do CRM — as mesmas chaves que o backend usa para achar a venda.
 */
export const hasHubPaymentRef = (order: { proposal_num?: number | null; order_num_ecommerce?: string | null }): boolean =>
  (order.proposal_num ?? 0) > 0 || /^\d+$/.test((order.order_num_ecommerce ?? '').trim());

/** Estorno aprovado mais recente (o comprovante do botão do cabeçalho), ou null. */
export const latestApprovedRefund = <T extends { status: number; credit_card_refund_id: number; created_on: string }>(
  refunds: T[],
): T | null =>
  refunds
    .filter((r) => r.status === 1)
    .reduce<T | null>(
      (best, r) =>
        !best || r.created_on > best.created_on || (r.created_on === best.created_on && r.credit_card_refund_id > best.credit_card_refund_id)
          ? r
          : best,
      null,
    );
